import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema } from './common.js';
import { ADMIN_ROLES, AdminRoleSchema, AdminUserStatusSchema } from './enums/identity.js';
import { AdminPageQuerySchema, adminPage } from './pagination.js';
/*
 * Admin authentication and admin users (05-api-specification.md § Admin surface,
 * 06-security-architecture.md § Admin authentication). Written provisionally in P03 for the admin
 * web forms; confirmed and completed in BACKEND-05.
 */
/** Minimum length for a new admin password (06 § Admin authentication). */
export const ADMIN_PASSWORD_MIN_LENGTH = 12;
/** Upper bound so a request cannot make the server hash an arbitrarily long string. */
export const ADMIN_PASSWORD_MAX_LENGTH = 128;
/** Name of the HttpOnly admin refresh cookie (path `/v1/admin/auth`). */
export const ADMIN_REFRESH_COOKIE = 'lions_admin_rt';
/** Path the refresh cookie is scoped to, so it only travels with the auth calls. */
export const ADMIN_REFRESH_COOKIE_PATH = '/v1/admin/auth';
/** Digits of an authenticator-app code (TOTP, 30-second steps). */
export const ADMIN_MFA_CODE_LENGTH = 6;
/** Recovery codes issued when MFA is enabled; each works once. */
export const ADMIN_MFA_RECOVERY_CODE_COUNT = 10;
/** Q-22 default: roles that must use MFA (configurable on the API with `ADMIN_MFA_ROLES`). */
export const ADMIN_MFA_DEFAULT_ROLES = ['SUPER_ADMIN', 'APPROVER'];
/** Admin email as typed into a form: trimmed and lower-cased before the format check. */
export const AdminEmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .pipe(z.email('Enter a valid email address'));
/** A new password (reset, change, invite); the breached-password check runs on the server. */
export const AdminNewPasswordSchema = z
    .string()
    .min(ADMIN_PASSWORD_MIN_LENGTH, `Use at least ${ADMIN_PASSWORD_MIN_LENGTH} characters`)
    .max(ADMIN_PASSWORD_MAX_LENGTH, `Use at most ${ADMIN_PASSWORD_MAX_LENGTH} characters`);
/** `POST /v1/admin/auth/login`. The password length is not checked here, only on new passwords. */
export const AdminLoginRequestSchema = z.strictObject({
    email: AdminEmailSchema,
    password: z.string().min(1, 'Enter your password').max(ADMIN_PASSWORD_MAX_LENGTH),
});
/**
 * `POST /v1/admin/auth/login`, `/auth/refresh` and `PATCH /auth/password` (200). The refresh token
 * travels only in the `HttpOnly` cookie scoped to `/v1/admin/auth`, never in a body.
 */
export const AdminAccessTokenSchema = z
    .object({
    accessToken: z.string().min(1),
    /** Seconds until the access token expires (15 minutes, 06 § Tokens). */
    expiresIn: z.number().int().positive(),
})
    .meta({ id: 'AdminAccessToken' });
/**
 * `meta` of the 401 `MFA_REQUIRED` answer to a correct password: the second step of the login.
 * `enrolmentRequired` means the admin has no authenticator yet and must call `/auth/mfa/enrol`
 * first (Q-22 default: mandatory for SUPER_ADMIN and APPROVER).
 */
export const AdminMfaRequiredMetaSchema = z.object({
    mfaToken: z.string().min(1),
    enrolmentRequired: z.boolean(),
    /** Seconds the `mfaToken` stays valid. */
    expiresIn: z.number().int().positive(),
});
/** `POST /v1/admin/auth/mfa/enrol`: a new authenticator secret for the login in progress. */
export const AdminMfaEnrolRequestSchema = z.strictObject({
    mfaToken: z.string().min(1).max(256),
});
export const AdminMfaEnrolmentSchema = z
    .object({
    /** Base32 secret for manual entry in the authenticator app. */
    secret: z.string().min(16),
    /** `otpauth://totp/...` URI (QR code content). */
    otpauthUri: z.string().startsWith('otpauth://'),
})
    .meta({ id: 'AdminMfaEnrolment' });
/** `POST /v1/admin/auth/mfa/verify`: an authenticator code or one unused recovery code. */
export const AdminMfaVerifyRequestSchema = z.strictObject({
    mfaToken: z.string().min(1).max(256),
    code: z
        .string()
        .trim()
        .min(ADMIN_MFA_CODE_LENGTH, 'Enter the code from your authenticator app')
        .max(32),
});
/** `POST /v1/admin/auth/mfa/verify` (200): tokens, plus recovery codes when MFA was just enabled. */
export const AdminMfaSessionSchema = z
    .object({
    accessToken: z.string().min(1),
    expiresIn: z.number().int().positive(),
    /** Shown once, right after enrolment; `null` on every later sign-in. */
    recoveryCodes: z.array(z.string()).nullable(),
})
    .meta({ id: 'AdminMfaSession' });
/** `GET /v1/admin/me`: the signed-in admin, with the roles that drive the menu. */
export const AdminMeSchema = z
    .object({
    id: IdSchema,
    email: z.email(),
    name: z.string(),
    roles: z.array(AdminRoleSchema).min(1),
    /** Seeded and invited admins must change their password before anything else. */
    mustChangePassword: z.boolean(),
    mfaEnabled: z.boolean(),
})
    .meta({ id: 'AdminMe' });
/** `POST /v1/admin/auth/forgot-password`: always 202, whether or not the account exists. */
export const AdminForgotPasswordRequestSchema = z.strictObject({
    email: AdminEmailSchema,
});
/** `POST /v1/admin/auth/reset-password` with the token from the emailed link (204). */
export const AdminResetPasswordRequestSchema = z.strictObject({
    token: z.string().min(1).max(512),
    newPassword: AdminNewPasswordSchema,
});
/** `PATCH /v1/admin/auth/password`: ends every other session and returns a fresh access token. */
export const AdminChangePasswordRequestSchema = z.strictObject({
    currentPassword: z.string().min(1, 'Enter your current password').max(ADMIN_PASSWORD_MAX_LENGTH),
    newPassword: AdminNewPasswordSchema,
});
/* --- Admin users (A-01 gap, SUPER_ADMIN only) -------------------------------------------- */
const AdminRolesSchema = z
    .array(AdminRoleSchema)
    .min(1, 'Choose at least one role')
    .max(ADMIN_ROLES.length)
    .refine((roles) => new Set(roles).size === roles.length, 'Each role only once');
export const AdminNameSchema = z.string().trim().min(2, 'Enter the full name').max(120);
/** One row of Settings › Admin Users. */
export const AdminUserSchema = z
    .object({
    id: IdSchema,
    email: z.email(),
    name: z.string(),
    roles: z.array(AdminRoleSchema),
    status: AdminUserStatusSchema,
    mustChangePassword: z.boolean(),
    mfaEnabled: z.boolean(),
    /** Set while the account is locked after failed logins. */
    lockedUntil: IsoDateTimeSchema.nullable(),
    lastLoginAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .meta({ id: 'AdminUser' });
/** `GET /v1/admin/admin-users?q&page&limit`, newest first. */
export const AdminUserListQuerySchema = AdminPageQuerySchema.extend({
    /** Matches the start of the email (case-insensitive). */
    q: z.string().trim().toLowerCase().min(1).max(100).optional(),
});
export const AdminUserPageSchema = adminPage(AdminUserSchema).meta({ id: 'AdminUserPage' });
/** `POST /v1/admin/admin-users` (201): a temporary password is emailed; it must be changed. */
export const AdminUserInviteRequestSchema = z.strictObject({
    email: AdminEmailSchema,
    name: AdminNameSchema,
    roles: AdminRolesSchema,
});
/** `PATCH /v1/admin/admin-users/{id}`: rename, set roles, disable or enable. */
export const AdminUserUpdateRequestSchema = z
    .strictObject({
    name: AdminNameSchema.optional(),
    roles: AdminRolesSchema.optional(),
    status: AdminUserStatusSchema.optional(),
})
    .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'Change at least one field',
});
//# sourceMappingURL=admin-auth.js.map